import { Group, Select, Text, type ComboboxLikeRenderOptionInput, type ComboboxItem } from '@mantine/core';
import { CountriesEnum } from '@routellers/types/dist/shared/enum/countries.enum';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { countryFlag } from '../utils/country-flag';

export const ALL_COUNTRIES = 'all';

type Props = {
    value: string;
    onChange: (value: string) => void;
    w?: number | string;
};

type CountryOption = ComboboxItem & { flag: string };

const OptionRow = ({ flag, label }: { flag: string; label: string }) => (
    <Group gap='sm' wrap='nowrap'>
        <Text span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden>
            {flag}
        </Text>
        <Text size='sm' truncate>
            {label}
        </Text>
    </Group>
);

export const CountrySelect = ({ value, onChange, w = 220 }: Props) => {
    const { t } = useTranslation();

    const data = useMemo<CountryOption[]>(() => {
        const countries = Object.values(CountriesEnum)
            .map((code) => ({
                value: code,
                label: t(`enums.countries.${code}`, {
                    defaultValue: code.toUpperCase(),
                }),
                flag: countryFlag(code),
            }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

        return [
            {
                value: ALL_COUNTRIES,
                label: t('components.countrySelect.allCountries'),
                flag: '🌍',
            },
            ...countries,
        ];
    }, [t]);

    const selected = data.find((row) => row.value === value) || data[0];

    const renderOption = ({ option }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
        const row = data.find((item) => item.value === option.value);
        return <OptionRow flag={row?.flag || '🌍'} label={option.label} />;
    };

    return (
        <Select
            w={w}
            data={data}
            value={value}
            onChange={(next) => onChange(next || ALL_COUNTRIES)}
            searchable
            clearable={value !== ALL_COUNTRIES}
            nothingFoundMessage={t('components.countrySelect.nothingFound')}
            placeholder={t('components.countrySelect.placeholder')}
            leftSection={
                <Text span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>
                    {selected.flag}
                </Text>
            }
            leftSectionWidth={36}
            renderOption={renderOption}
            styles={{
                input: { paddingLeft: 40 },
            }}
        />
    );
};
